import { instrumentJourneyMutation } from '../journeyInstrumentation';
import { beginSubmissionRequest, checkSubmissionMaintenance } from '../utils/submissionMaintenance';
import {
  deleteProfileCookies,
  getAuthToken,
  getUserRole,
} from '../userLocalDetails';
import { toast } from 'react-toastify';
import Toast from '../components/Toast';
import {
  isEmbeddedMode,
  notifyEmbeddedSessionExpired,
} from '../utils/ltiEmbedded';
import { endSessionKeepingTeacherLoop } from '../utils/teacherLoopSession';

export const baseUrl = (
  process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8080'
).replace(/\/+$/, '');
export const jeddleBaseUrl =
  process.env.REACT_APP_JEDDLE_BASE_URL ?? 'https://jeddle.duxdigital.net';
export const selfBaseUrl =
  process.env.REACT_APP_SELF_BASE_URL ?? 'http://localhost:1234';
export const clientId =
  process.env.REACT_APP_CLIENT_ID ?? 'HyULSQEsXGFXmaOAzohGYFcvoegcxFaY';
export const env = process.env.REACT_APP_ENV ?? 'dev';

const debugEnabled =
  (process.env.REACT_APP_DEBUG_LOADING ?? '').toLowerCase() === 'true' ||
  (process.env.NODE_ENV ?? '').toLowerCase() === 'development';
export const logDebug = (...args) => {
  if (debugEnabled) {
    console.log('[service]', ...args);
  }
};

export let isLoggedOut = false;
export const setLoggedOut = (val) => { isLoggedOut = val; };

export const isLoggedIn = () => {
  const token = localStorage.getItem('userId');
  return token !== null && token !== undefined;
};

export const isTeacher = (role = null) => {
  const userRole = role || getUserRole();
  return Boolean(userRole) && String(userRole).toUpperCase() === 'TEACHER';
};

const isLocalhost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1');

const handleRedirect = (redirectUrl) => {
  if (!redirectUrl) return;
  if (isLocalhost()) {
    try {
      const redirectHost = new URL(redirectUrl.replace(/['"]+/g, '')).hostname;
      if (redirectHost !== 'localhost' && redirectHost !== '127.0.0.1') return;
    } catch {
      return;
    }
  }
  const cleanUrl = redirectUrl.replace(/['"]+/g, '');
  setTimeout(() => {
    if (window.location.href === cleanUrl) {
      window.location.reload();
    } else {
      window.location.href = cleanUrl;
    }
  }, 4000);
};

export async function fetchData(url, options = {}, headers = {}) {
  const maintenanceTicket = beginSubmissionRequest();
  const token = getAuthToken();

  const createHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...headers,
    };
  };

  try {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers: createHeaders(),
    });

    await checkSubmissionMaintenance(response, url, options, maintenanceTicket);

    const errorMessage = response.headers.get('x-error-message');
    const redirectUrl = response.headers.get('x-redirect-url');

    if (!response.ok) {
      console.error('API request failed:', response);
      if (response.status === 401) {
        if (isEmbeddedMode()) {
          // Embedded in an LMS iframe: do NOT wipe storage or navigate the frame.
          // The LMS owns the session; surface a "relaunch from your LMS" state instead.
          notifyEmbeddedSessionExpired();
          return;
        }
        if (process.env.REACT_APP_TEACHER_LOOP === 'on') {
          // Keeps the jeddle.tl.* keys; on a Teacher Loop public page, stays
          // and reloads logged out (utils/teacherLoopSession.js).
          if (!endSessionKeepingTeacherLoop(deleteProfileCookies)) {
            window.location.href = '/';
          }
          return;
        }
        deleteProfileCookies();
        localStorage.clear();
        window.location.href = '/';
        return;
      }

      if (errorMessage) {
        toast(<Toast message={errorMessage} />);
      }

      handleRedirect(redirectUrl);

      const errorData = await response.json();
      throw new Error(errorData.message || 'API request failed');
    }

    return response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

async function modifyDataRaw(url, options = {}) {
  const maintenanceTicket = beginSubmissionRequest();
  const token = getAuthToken();
  const suppressToast = options.suppressToast || false;
  const errorToastState = { shown: false };

  const createHeaders = () => {
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };
  };

  try {
    const response = await fetch(url, {
      ...options,
      credentials: options.credentials ?? 'include',
      headers: createHeaders(),
    });

    await checkSubmissionMaintenance(response, url, options, maintenanceTicket);

    const errorMessage = response.headers.get('x-error-message');
    const successMessage = response.headers.get('x-success-message');
    const redirectUrl = response.headers.get('x-redirect-url');
    const forceRefresh = response.headers.get('x-force-refresh');

    if (!response.ok) {
      // Embedded in an LMS iframe + 401: do NOT wipe storage, reload the frame,
      // navigate via x-redirect-url, or toast — any of those would tear down /
      // navigate the iframe before the "relaunch from your LMS" notice can show.
      // The LMS owns the session; surface the relaunch state instead. This must
      // run BEFORE the force-refresh / redirect / toast handling below.
      if (response.status === 401 && isEmbeddedMode()) {
        notifyEmbeddedSessionExpired();
        return;
      }

      let finalErrorMessage;

      try {
        const errorData = await response.json();
        finalErrorMessage =
          errorMessage ||
          errorData.message ||
          `Error occurred: ${response.status}`;
      } catch {
        finalErrorMessage =
          errorMessage || `Error occurred: ${response.status}`;
      }
      if (!suppressToast) {
        toast(<Toast message={finalErrorMessage} />);
        errorToastState.shown = true;
      }
      if (forceRefresh && forceRefresh === 'true') {
        window.location.reload();
      }
      handleRedirect(redirectUrl);

      if (response.status === 401) {
        if (process.env.REACT_APP_TEACHER_LOOP === 'on') {
          // Keeps the jeddle.tl.* keys; on a Teacher Loop public page, stays
          // and reloads logged out (utils/teacherLoopSession.js).
          if (!endSessionKeepingTeacherLoop(deleteProfileCookies)) {
            window.location.href = '/';
          }
          return;
        }
        deleteProfileCookies();
        localStorage.clear();
        window.location.href = '/';
        return;
      }

      throw new Error(finalErrorMessage);
    }

    if (successMessage && !suppressToast) {
      toast(<Toast message={successMessage} type="success" />);
    }

    handleRedirect(redirectUrl);

    const responseText = await response.text();
    let responseData = null;
    if (responseText && responseText.trim() !== '') {
      try {
        responseData = JSON.parse(responseText);
      } catch (parseError) {
        responseData = null;
      }
    }

    return {
      data: responseData,
      successMessage: successMessage,
      errorMessage: null,
    };
  } catch (error) {
    console.error('Fetch error:', error);
    if (!suppressToast && !errorToastState.shown && !error.maintenanceHandled) {
      toast(<Toast message={error.message} />);
    }
    throw error;
  }
}

export const modifyData = (url, options = {}) =>
  instrumentJourneyMutation(url, options.method, (correlation) =>
    modifyDataRaw(
      url,
      correlation ? { ...options, headers: { ...options.headers, ...correlation } } : options
    )
  );

export const fetchApi = async (url, options, headers) => {
  return fetchData(url, options, headers);
};

export const getApi = async (url) => fetchApi(url, { method: 'GET' });

export const postApi = async (url, body, options = {}) => {
  try {
    const response = await modifyData(url, {
      method: 'POST',
      body: JSON.stringify(body),
      suppressToast: options.suppressToast || false,
    });

    if (options.includeMessages) {
      return response;
    }
    return response?.data;
  } catch (error) {
    if (error.maintenanceHandled) throw error;
    console.error('Post API error:', error);
    if (options.includeMessages) {
      return {
        data: null,
        successMessage: null,
        errorMessage: error.message,
      };
    }
    return null;
  }
};

export const putApi = async (url, body) => {
  const token = getAuthToken();
  const response = await modifyData(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return response?.data;
};

export const patchApi = async (url, body) => {
  const token = getAuthToken();
  const response = await modifyData(url, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return response?.data;
};

export const deleteApi = async (url) => {
  const token = getAuthToken();
  const response = await modifyData(url, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  return response?.data;
};

export const normalizeYear = (y) => {
  if (typeof y === 'number' && !Number.isNaN(y)) return y;
  const s = String(y || '')
    .replace(/year\s*/i, '')
    .trim();
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? undefined : n;
};

export const toApiSubject = (s) =>
  s && String(s).toLowerCase() === 'unassigned' ? 'UNASSIGNED' : s;
export const toApiYear = (y) =>
  y != null && String(y).toLowerCase() === 'unassigned'
    ? 'UNASSIGNED'
    : normalizeYear(y);
export const enc = (v) => encodeURIComponent(String(v));

export const buildQueryString = (filters = {}) => {
  const parts = [];
  const codeList =
    filters.subjectCodes ??
    (filters.subjectCode ? [filters.subjectCode] : []) ??
    filters.subjects ??
    (filters.subject ? [filters.subject] : []);
  const codes = codeList.filter((s) => s).map((s) => toApiSubject(s));
  if (codes.length === 1) parts.push('subjectCode=' + enc(codes[0]));
  else codes.forEach((c) => parts.push('subjectCodes=' + enc(c)));
  codes.forEach((s) => s && parts.push('subjects=' + enc(s)));
  if (filters.state) parts.push('state=' + enc(filters.state));
  const yearsRaw =
    filters.years ??
    (filters.year !== undefined && filters.year !== null ? [filters.year] : []);
  const yearsProcessed = [...yearsRaw].map(toApiYear);
  yearsProcessed.forEach((y) => y != null && parts.push('years=' + enc(y)));
  const taskTypes =
    filters.taskTypes || (filters.taskType ? [filters.taskType] : []);
  taskTypes.forEach((t) => t && parts.push('taskTypes=' + enc(t)));
  const responseTypes =
    filters.responseTypes ||
    (filters.responseType ? [filters.responseType] : []);
  responseTypes.forEach((r) => r && parts.push('responseTypes=' + enc(r)));
  const order =
    filters.sortOrder === 'newToOld' || filters.sortOrder === 'oldToNew'
      ? filters.sortOrder
      : null;
  if (order) parts.push('sortOrder=' + enc(order));
  return parts.length ? '?' + parts.join('&') : '';
};

export const getTemplateRecommendations = async (params) => {
  const { subject, taskType, responseType, year, state } = params;

  const queryParams = new URLSearchParams();
  if (subject) queryParams.append('subject', subject);
  if (taskType) queryParams.append('taskType', taskType);
  if (responseType) queryParams.append('responseType', responseType);
  if (year) {
    const normalized = normalizeYear(year);
    if (normalized != null) queryParams.append('year', normalized);
  }
  if (state) queryParams.append('state', state);

  const url = `${baseUrl}/teachers/templates/recommendations?${queryParams.toString()}`;

  try {
    const response = await fetchData(url);
    return response || {
      commentBankId: null,
      successCriteriaIds: [],
      shortAnswerIds: [],
      markingCriteriaId: null,
    };
  } catch (error) {
    console.error('Failed to fetch template recommendations:', error);
    return {
      commentBankId: null,
      successCriteriaIds: [],
      shortAnswerIds: [],
      markingCriteriaId: null,
    };
  }
};
